import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { IdentityProvider, useIdentity } from '../auth/useIdentity';
import { AxiosProvider } from '../hooks/useAxiosSwagger';
import { AlertsProvider } from '../hooks/useAlerts';
import { BusyProvider } from '../hooks/useBusy';
import { CockpitProvider } from './data/CockpitContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Shell } from './shell/Shell';
import './v2.scss';

const FONTS = 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap';

/** Loads the two typefaces of v2 once; the old pages keep theirs. */
const useFonts = () => {
    useEffect(() => {
        if (document.querySelector(`link[href="${FONTS}"]`)) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = FONTS;
        document.head.appendChild(link);
    }, []);
};

const Gate = () => {
    const { status, error, isAdmin } = useIdentity();

    if (status === 'loading') {
        return (
            <div className="v2" style={{ padding: 40 }}>
                <span className="mut">Laden…</span>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="v2" style={{ padding: 40 }}>
                <div className="notice">Wie je bent kon niet worden opgehaald: {error}</div>
            </div>
        );
    }

    // Support only for now: a customer account keeps the old dashboard and its error log there.
    if (!isAdmin) return <Navigate replace to="/" />;

    return (
        <ErrorBoundary where="dashboard">
            <CockpitProvider>
                <Shell />
            </CockpitProvider>
        </ErrorBoundary>
    );
};

/**
 * The v2 dashboard: the same sign-in, token and providers as the old app, its own frame. Lives at /v2 next to
 * the old pages until every screen has moved.
 */
export const V2App = () => {
    const { isLoading, error, isAuthenticated, loginWithRedirect } = useAuth();
    useFonts();

    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            loginWithRedirect({ appState: { returnTo: window.location.pathname + window.location.search } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    if (error) return <div className="v2" style={{ padding: 40 }}><div className="notice">Aanmelden mislukt: {error.message}</div></div>;
    if (isLoading || !isAuthenticated) return <div className="v2" style={{ padding: 40 }}><span className="mut">Aanmelden…</span></div>;

    return (
        <AxiosProvider>
            <IdentityProvider>
                <AlertsProvider>
                    <BusyProvider>
                        <Gate />
                    </BusyProvider>
                </AlertsProvider>
            </IdentityProvider>
        </AxiosProvider>
    );
};
